import VaultActionAttention from "../common/VaultActionAttention";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { BADGES } from "../../constants/badges";
import { useTranslation } from "react-i18next";
import EventMetaIcon from "./EventMetaIcon";
import { getEventWeekdayAbbr } from "../../utils/eventDateLabels";

// Helpers for formatting date on the card: HH:MM DD/MM DOW(two letters)
const pad2 = (n) => String(n).padStart(2, '0');

// Fine‑tuning vertical offsets (in px) for alignment
const TITLE_Y_OFFSET = 2;     // move event title slightly up
const DATETIME_Y_OFFSET = 5;   // move datetime slightly down
const HEART_Y_OFFSET = -2;     // move heart icon slightly up

function tryParseDate(input) {
  if (!input) return null;
  if (input instanceof Date && !isNaN(input)) return input;

  // Parse deterministically; avoid native parsing of ambiguous strings like "11/10/25"
  // 1) If ISO-like (YYYY-MM-DD ...), let Date handle it.
  if (typeof input === 'string') {
    if (/^\d{4}-\d{2}-\d{2}[T\s]/.test(input)) {
      const iso = new Date(input);
      if (!isNaN(iso)) return iso;
    }

    // 2) Handle formats like "HH:MM DD/MM/YY" or "HH:MM DD.MM.YYYY" (EU order: DD then MM)
    let m = input.match(/^(\d{1,2}):(\d{2})\s+(\d{1,2})[./-](\d{1,2})(?:[./-](\d{2,4}))?(?:\s+\S+)?$/u);
    if (m) {
      let [, hh, mm, dd, MM, yy] = m;
      const year = yy ? (yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10)) : (new Date()).getFullYear();
      return new Date(parseInt(year, 10), parseInt(MM, 10) - 1, parseInt(dd, 10), parseInt(hh, 10), parseInt(mm, 10));
    }

    // 3) Handle formats without time: "DD/MM/YY" or "DD.MM.YYYY"
    m = input.match(/^(\d{1,2})[./-](\d{1,2})(?:[./-](\d{2,4}))?(?:\s+\S+)?$/u);
    if (m) {
      let [, dd, MM, yy] = m;
      const year = yy ? (yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10)) : (new Date()).getFullYear();
      return new Date(parseInt(year, 10), parseInt(MM, 10) - 1, parseInt(dd, 10));
    }
  }
  return null;
}

function buildDatetimeDisplayOptions(input, language) {
  const d = tryParseDate(input);
  if (!d) {
    // Fallback: if we can't parse, strip trailing year if present and return as-is
    if (typeof input === 'string') {
      const stripped = input.replace(/([/.-])\d{2,4}$/, '').trim();
      const withoutWeekday = stripped.replace(/\s+[\p{L}.]{2,}$/u, "").trim();
      const dateOnlyMatch = withoutWeekday.match(/(\d{1,2}[./-]\d{1,2})(?:[./-]\d{2,4})?$/);
      return {
        full: stripped,
        dateTime: withoutWeekday || stripped,
        dateOnly: dateOnlyMatch?.[1] || withoutWeekday || stripped,
      };
    }
    const fallback = String(input ?? '');
    return { full: fallback, dateTime: fallback, dateOnly: fallback };
  }
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  const day = pad2(d.getDate());
  const month = pad2(d.getMonth() + 1);
  const dow = getEventWeekdayAbbr(d.getDay(), language);
  return {
    full: `${hh}:${mm} ${day}/${month} ${dow}`,
    dateTime: `${hh}:${mm} ${day}/${month}`,
    dateOnly: `${day}/${month}`,
  };
}

const EventCard = ({
  title,
  avatar,
  isOnline,
  isPersonal,
  isPaid,
  isByRequest = false,
  datetime,
  isVerified,
  isValidated,
  color = "#ffffff",
  isFavourite,
  onToggleFavourite,
  isSelected = false,
  fromMap = false,
  status,
  badgeLabel,
  iconImageUrl,
  isPromoted = false,
  isMainEvent = false,
  isPlatformEvent = false,
  isCurrentEvent = false,
  platformAccentIndex = 0,
  unreadCount = 0,
  type,
  titleMaxWidthClass,
  isGhostConcept = false,
  ghostConceptSoft = false,
  subtitleText = "",
  hideMetaIcons = false,
  hideDefaultConceptIcons = false,
  showFavouriteButton = true,
  topRightBadge = null,
  topRightBadgeClassName = "",
  topCenterBadge = null,
  topCenterBadgeClassName = "",
  topCenterBadgePlacement = "center",
  showSeriesToggle = false,
  isSeriesExpanded = false,
  onToggleSeriesExpand,
  expandableContent = null,
  disablePlatformHighlight = false,
  cardClassName = "",
  attention = false,
  suppressStatusOverlay = false,
  datetimeDisplayMode = "date_time",
  searchListLayout = false,
}) => {
  const { t, i18n } = useTranslation("events");
  const footerRef = useRef(null);
  const metaRef = useRef(null);
  const datetimeMeasureRef = useRef(null);
  const [datetimeFitMode, setDatetimeFitMode] = useState("full");

  const getBadgeSrc = (label) => {
    const badge = BADGES.find(b => b.id === label);
    return badge?.src || "/assets/event_pics/default.webp";
  };

  const st = typeof status === "string" ? status.toLowerCase() : "";
  const isExpiredUnvalidated = st === "expired_unvalidated";
  const isCancelled = st === "cancelled";
  const isDeleted = st === 'deleted';
  const isPending = isValidated === false && !isExpiredUnvalidated && !isCancelled && !isDeleted;
  const isFinished = st === 'finished';
  const hasStatusOverlay = isDeleted || isPending || isFinished || isExpiredUnvalidated || isCancelled;
  const badgeSrc = String(iconImageUrl || "").trim() || (badgeLabel ? getBadgeSrc(badgeLabel) : "");
  const hasBadge = Boolean(badgeSrc);
  const isDesktop = useIsDesktop();
  const showGhostConceptHighlight =
    Boolean(isGhostConcept) &&
    type === "concept" &&
    !isDeleted &&
    !isCancelled &&
    !isPending &&
    !isFinished &&
    !isExpiredUnvalidated;
  const ghostBackgroundClass = ghostConceptSoft
    ? "bg-[linear-gradient(120deg,rgba(11,45,89,0.5)_0%,rgba(7,31,68,0.48)_45%,rgba(4,23,51,0.44)_100%)]"
    : "bg-[linear-gradient(120deg,rgba(11,45,89,0.94)_0%,rgba(7,31,68,0.94)_45%,rgba(4,23,51,0.92)_100%)]";
  const ghostShadowClass = ghostConceptSoft
    ? "shadow-[0_0_20px_rgba(0,115,255,0.2)]"
    : "shadow-[0_0_24px_rgba(0,115,255,0.42)]";

  const datetimeDisplayOptions = useMemo(
    () => buildDatetimeDisplayOptions(datetime, i18n?.resolvedLanguage || i18n?.language),
    [datetime, i18n?.resolvedLanguage, i18n?.language]
  );
  const shouldAdaptDatetime =
    type !== "concept" && datetimeDisplayMode !== "date_only";
  const datetimeDisplay =
    datetimeDisplayMode === "date_only"
      ? datetimeDisplayOptions.dateOnly
      : shouldAdaptDatetime
        ? datetimeDisplayOptions[datetimeFitMode] || datetimeDisplayOptions.full
        : datetimeDisplayOptions.full;

  const unread = Number(unreadCount || 0);
  const showUnreadBadge = unread > 0;
  // Allow 3 digits (e.g. 420). Hard-cap only for extreme cases.
  const unreadLabel = unread > 999 ? "999+" : String(unread);
  const resolvedTitleMaxWidthClass =
    titleMaxWidthClass || (isDesktop ? "max-w-[500px]" : "max-w-[250px]");
  const showPlatformEventHighlight =
    isPlatformEvent &&
    !disablePlatformHighlight &&
    !isDeleted &&
    !isCancelled &&
    !isPending &&
    !isFinished &&
    !isExpiredUnvalidated;
  const isMapPlatformCard = showPlatformEventHighlight && fromMap && !searchListLayout;

  const borderClass = isCurrentEvent && !showPlatformEventHighlight
    ? searchListLayout
      ? "border-0 shadow-[0_0_0_2px_rgba(52,211,153,0.95)]"
      : "border-2 border-emerald-400"
    : showPlatformEventHighlight
    ? (isMapPlatformCard
        ? "border-2 border-white"
        : searchListLayout
          ? "border-0"
          : "border-2 border-transparent")
    : isSelected
    ? (searchListLayout
        ? "border-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.25)_0%,rgba(255,255,255,0)_100%)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)]"
        : fromMap
        ? "border-2 border-white"
        : "border-2 border-blue-500")
    : showGhostConceptHighlight
      ? searchListLayout
        ? "border-0 shadow-[0_0_0_1px_rgba(166,207,255,0.80)]"
        : "border border-solid border-[#A6CFFF] border-opacity-80"
      : searchListLayout
        ? "border-0 shadow-[0_0_0_1px_rgba(255,255,255,0.50)]"
        : "border border-solid border-white border-opacity-50";

  // Featured treatment is visual only: a soft inner glow without a text badge.
  const showFeaturedHighlight =
    isPromoted &&
    !showSeriesToggle &&
    !isPlatformEvent &&
    !isMainEvent &&
    !isDeleted &&
    !isCancelled &&
    !isPending &&
    !isFinished &&
    !isExpiredUnvalidated;

  // Для главного ивента делаем внешний blue‑glow через box-shadow
  const mainEventGlowClass =
    isMainEvent && !isDeleted && !isCancelled && !isPending && !isFinished && !isExpiredUnvalidated
      ? "z-10"
      : "";
  const platformEventHighlight =
    showPlatformEventHighlight
      ? (
          isMapPlatformCard
            ? ""
            : fromMap && !searchListLayout
            ? "vg-platform-card vg-platform-card-map"
            : "vg-platform-card"
        )
      : "";
  const platformEventStyle = showPlatformEventHighlight && !isMapPlatformCard
    ? {
        "--vg-platform-border": isCurrentEvent
          ? "rgba(52,211,153,0.98)"
          : isSelected
          ? "rgba(59,130,246,0.96)"
          : "rgba(255,255,255,0.96)",
        "--vg-platform-glow": isCurrentEvent
          ? "rgba(16,185,129,0.28)"
          : isSelected
          ? "rgba(59,130,246,0.28)"
          : "rgba(255,255,255,0.18)",
        "--vg-platform-outline": isCurrentEvent
          ? "rgba(52,211,153,0.55)"
          : isSelected
          ? "rgba(59,130,246,0.95)"
          : "rgba(255,255,255,0.22)",
      }
    : undefined;
  const hasExpandableBlock = Boolean(showSeriesToggle && expandableContent);
  const cardPaddingClass = searchListLayout ? "p-0" : "px-2 py-2";
  const cardHeightClass = hasExpandableBlock
    ? "min-h-[74px]"
    : searchListLayout ? "h-[75px]" : "h-[74px]";
  const listMediaFrameClass = isCurrentEvent
    ? "vg-current-event-accent vg-current-event-media-frame shadow-[0_0_0_1px_rgba(52,211,153,0.55)]"
    : isSelected && searchListLayout
    ? "shadow-[0_0_0_4px_rgba(59,130,246,0.96),0_0_0_5px_rgba(59,130,246,0.28)]"
    : showPlatformEventHighlight
      ? "shadow-[0_0_0_4px_rgba(255,255,255,0.96)]"
      : "shadow-[0_0_0_1px_rgba(255,255,255,0.78)]";
  const listMediaFrameBaseClass =
    "relative isolate mr-[12px] h-[75px] w-[75px] flex-shrink-0 overflow-hidden rounded-[16px] bg-white";
  const mediaClass = searchListLayout
    ? `block h-full w-full object-cover ${isCurrentEvent ? "rounded-[13px]" : ""}`
    : `mr-2 rounded-full h-[55px] w-[55px] object-cover flex-shrink-0 ${isCurrentEvent ? "ring-2 ring-emerald-400" : ""}`;
  const placeholderClass = searchListLayout
    ? `h-full w-full ${isCurrentEvent ? "rounded-[13px]" : ""}`
    : `mr-2 rounded-full h-[55px] w-[55px] flex-shrink-0 ${isCurrentEvent ? "ring-2 ring-emerald-400" : ""}`;
  const rowClass = searchListLayout ? "h-[75px]" : "";
  const contentClass = searchListLayout
    ? "h-[75px] py-[8px] pr-[10px]"
    : "h-[55px]";
  const titleClass = searchListLayout
    ? "text-[18px] font-semibold leading-[22px]"
    : "text-lg font-extrabold";
  const metaIconClass = searchListLayout
    ? "w-[22px] h-[22px]"
    : "w-6 h-6";
  const metaGapClass = "gap-2";
  const datetimeClass = searchListLayout ? "text-[14px] leading-[22px]" : "text-sm";
  const favouriteIconClass = "w-5 h-5";
  const favouriteButtonClass = searchListLayout
    ? "ml-1 flex h-[22px] w-[22px] shrink-0 items-end justify-center"
    : "ml-1";
  const footerGapClass = "gap-6";
  const titleYOffset = searchListLayout ? 0 : TITLE_Y_OFFSET;
  const heartYOffset = searchListLayout ? 0 : HEART_Y_OFFSET;
  const datetimeYOffset = searchListLayout ? 0 : DATETIME_Y_OFFSET;
  const titleRowAlignClass = searchListLayout ? "items-end" : "items-start";
  const footerAlignClass = searchListLayout ? "items-end" : "items-center";
  const metaAlignClass = searchListLayout ? "items-end" : "items-center";
  const topCenterBadgeTranslateClass =
    topCenterBadgePlacement === "above" ? "-translate-y-full" : "-translate-y-1/2";

  useEffect(() => {
    if (!shouldAdaptDatetime) {
      setDatetimeFitMode("full");
      return undefined;
    }

    const footerNode = footerRef.current;
    const metaNode = metaRef.current;
    const measureNode = datetimeMeasureRef.current;
    if (!footerNode || !measureNode) return undefined;

    const measureTextWidth = (value) => {
      measureNode.textContent = value || "";
      return measureNode.getBoundingClientRect().width;
    };

    const updateFitMode = () => {
      const footerWidth = footerNode.getBoundingClientRect().width;
      const metaWidth = metaNode?.getBoundingClientRect().width || 0;
      const footerStyle = window.getComputedStyle(footerNode);
      const gap = Number.parseFloat(footerStyle.columnGap || footerStyle.gap || "0") || 0;
      const availableWidth = Math.max(0, footerWidth - metaWidth - gap);
      const widthPadding = 1;

      const nextMode =
        measureTextWidth(datetimeDisplayOptions.full) <= availableWidth + widthPadding
          ? "full"
          : measureTextWidth(datetimeDisplayOptions.dateTime) <= availableWidth + widthPadding
            ? "dateTime"
            : "dateOnly";

      setDatetimeFitMode((prevMode) => (prevMode === nextMode ? prevMode : nextMode));
    };

    updateFitMode();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateFitMode);
      return () => window.removeEventListener("resize", updateFitMode);
    }

    const resizeObserver = new ResizeObserver(updateFitMode);
    resizeObserver.observe(footerNode);
    if (metaNode) resizeObserver.observe(metaNode);
    window.addEventListener("resize", updateFitMode);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateFitMode);
    };
  }, [
    datetimeDisplayOptions.dateOnly,
    datetimeDisplayOptions.dateTime,
    datetimeDisplayOptions.full,
    hideMetaIcons,
    isPaid,
    isVerified,
    isByRequest,
    shouldAdaptDatetime,
    subtitleText,
  ]);

  return (
    <article
      style={platformEventStyle}
      className={`relative flex flex-col rounded-[16px] ${cardPaddingClass} ${borderClass} ${mainEventGlowClass} ${platformEventHighlight} ${cardClassName} ${cardHeightClass}`}
    >
      <VaultActionAttention active={attention} outline className="inset-0" />
      {topRightBadge ? (
        <div
          className={`absolute right-3 top-0 z-20 -translate-y-1/2 rounded-full border ${fromMap ? "bg-[#2E67E8]" : "bg-[#13233b]"} px-4 py-1 text-[15px] font-extrabold ${topRightBadgeClassName}`}
        >
          {topRightBadge}
        </div>
      ) : null}
      {showUnreadBadge && (
        <div
          className="pointer-events-none absolute -left-2 -top-2 z-20 bg-white flex items-center justify-center h-[18px] min-w-[18px] px-[5px] rounded-full"
          aria-hidden="true"
        >
          <span className="text-[#0062D9] text-[11px] font-extrabold leading-none">
            {unreadLabel}
          </span>
        </div>
      )}
      {isMainEvent && !isDeleted && !isCancelled && !isPending && !isFinished && !isExpiredUnvalidated && (
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl -z-10 shadow-[0_0_20px_1px_rgba(0,115,255,0.25)] bg-transparent"
          aria-hidden="true"
        />
      )}
      {showFeaturedHighlight && (
        <div
          data-testid="featured-event-highlight"
          className="pointer-events-none absolute inset-0 z-0 rounded-[16px] bg-[linear-gradient(180deg,rgba(255,255,255,0.25)_0%,rgba(255,255,255,0)_100%)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)]"
          aria-hidden="true"
        />
      )}
      {showPlatformEventHighlight && !searchListLayout && (
        <div
          className={`pointer-events-none absolute inset-[1px] rounded-[15px] ${
            isMapPlatformCard
              ? "bg-transparent"
              : "bg-[radial-gradient(120%_100%_at_0%_0%,rgba(255,255,255,0.12)_0%,rgba(255,255,255,0)_38%),linear-gradient(180deg,rgba(10,21,40,0.92)_0%,rgba(7,17,33,0.96)_100%)]"
          }`}
          aria-hidden="true"
        />
      )}
      {isSelected && searchListLayout && !fromMap ? (
        <>
          <span
            className="pointer-events-none absolute left-[calc(0px_-_var(--vg-page-gutter)_-_5px)] top-1/2 z-20 h-[21px] w-[14px] -translate-y-1/2 bg-white [clip-path:polygon(0_0,100%_50%,0_100%)] lg:left-[-26px]"
            aria-hidden="true"
          />
          <span
            className="pointer-events-none absolute right-[calc(0px_-_var(--vg-page-gutter)_-_5px)] top-1/2 z-20 h-[21px] w-[14px] -translate-y-1/2 bg-white [clip-path:polygon(100%_0,0_50%,100%_100%)] lg:right-[-26px]"
            aria-hidden="true"
          />
        </>
      ) : null}
      {showGhostConceptHighlight && (
        <>
          <div
            className={`pointer-events-none absolute inset-0 rounded-2xl -z-0 ${ghostBackgroundClass}`}
            aria-hidden="true"
          />
          <div
            className={`pointer-events-none absolute inset-0 rounded-2xl -z-0 ${ghostShadowClass}`}
            aria-hidden="true"
          />
        </>
      )}
      {topCenterBadge ? (
        <div
          className={`absolute left-1/2 top-0 z-10 -translate-x-1/2 ${topCenterBadgeTranslateClass}`}
        >
          <div
            className={`px-3.5 py-0.5 rounded-full border border-white/60 text-[10px] leading-4 font-extrabold tracking-[0.18em] uppercase text-white whitespace-nowrap ${fromMap ? "bg-[#2E67E8]" : "bg-[#0E1424]"} ${topCenterBadgeClassName}`}
          >
            {topCenterBadge}
          </div>
        </div>
      ) : null}
      {!suppressStatusOverlay && hasStatusOverlay && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none rounded-2xl">
          {isPending ? (
            <div className="px-3 text-center text-white">
              <p className="text-[16px] font-bold leading-5">
                {t("event_card.status_pending")}
              </p>
              <p className="mt-0.5 text-[11px] font-semibold leading-4 text-white/80">
                {t("event_card.status_pending_note")}
              </p>
            </div>
          ) : (
            <span className="text-center text-lg font-bold text-white">
              {isDeleted || isCancelled
                ? t("event_card.status_cancelled")
                : isExpiredUnvalidated
                  ? t("event_card.status_expired_unvalidated")
                  : t("event_card.status_finished")}
            </span>
          )}
        </div>
      )}
      <div className={`relative z-10 flex items-start w-full ${rowClass} ${(!suppressStatusOverlay && hasStatusOverlay) ? 'opacity-15' : ''}`}>
        {searchListLayout ? (
          <div className={`${listMediaFrameBaseClass} ${listMediaFrameClass}`}>
            {hasBadge ? (
              <img
                src={badgeSrc}
                alt={t("event_card.thumbnails.badge_alt", { title })}
                className={mediaClass}
              />
            ) : avatar ? (
              <img
                src={avatar}
                alt={t("event_card.thumbnails.avatar_alt", { title })}
                className={mediaClass}
              />
            ) : (
              <div
                className={placeholderClass}
                style={{ backgroundColor: color }}
                role="img"
                aria-label={t("event_card.thumbnails.placeholder_alt", { title })}
              />
            )}
          </div>
        ) : hasBadge ? (
          <img
            src={badgeSrc}
            alt={t("event_card.thumbnails.badge_alt", { title })}
            className={mediaClass}
          />
        ) : avatar ? (
          <img
            src={avatar}
            alt={t("event_card.thumbnails.avatar_alt", { title })}
            className={mediaClass}
          />
        ) : (
          <div
            className={placeholderClass}
            style={{ backgroundColor: color }}
            role="img"
            aria-label={t("event_card.thumbnails.placeholder_alt", { title })}
          />
        )}

        <div className={`flex-1 flex flex-col justify-between min-w-0 ${contentClass}`}>
          <div className={`flex ${titleRowAlignClass} justify-between gap-2`}>
            <div className="min-w-0 flex-1">
              <h3
                className={`${titleClass} truncate ${resolvedTitleMaxWidthClass} text-white`}
                style={{ transform: `translateY(${titleYOffset}px)` }}
              >
                {title}
              </h3>
            </div>
            {showFavouriteButton && (
              <button
                className={favouriteButtonClass}
                aria-label={t("event_card.actions.favourite_aria")}
                onClick={onToggleFavourite}
                style={{ transform: `translateY(${heartYOffset}px)` }}
              >
                <img
                  src={`/assets/events/${isFavourite ? 'like_icon_on.svg' : 'like_icon_off.svg'}`}
                  alt={t("event_card.icons.like_alt")}
                  className={favouriteIconClass}
                />
              </button>
            )}
          </div>
          <div
            ref={footerRef}
            className={`relative flex ${footerAlignClass} justify-between ${footerGapClass} min-w-0`}
          >
            {hideMetaIcons ? (
              <div
                ref={metaRef}
                className="min-w-0 truncate text-[14px] font-semibold text-white text-opacity-50"
              >
                {subtitleText}
              </div>
            ) : (
              <div ref={metaRef} className={`flex ${metaAlignClass} ${metaGapClass}`}>
                {!hideDefaultConceptIcons && (
                  <EventMetaIcon
                    src={`/assets/events/${isOnline ? 'online_icon.svg' : 'offline_icon.svg'}`}
                    alt={isOnline
                      ? t("event_card.icons.online_alt")
                      : t("event_card.icons.offline_alt")}
                    className={metaIconClass}
                  />
                )}
                <EventMetaIcon
                  src={`/assets/events/${isPersonal ? 'personal_icon.svg' : 'organization_icon.svg'}`}
                  alt={
                    isPersonal
                      ? t("event_card.icons.personal_alt")
                      : t("event_card.icons.organization_alt")
                  }
                  className={metaIconClass}
                />
                {isPaid && (
                  <EventMetaIcon
                    src="/assets/events/payable_icon.svg"
                    alt={t("event_card.icons.paid_alt")}
                    className={metaIconClass}
                  />
                )}
                {isVerified && (
                  <EventMetaIcon
                    src="/assets/events/verified_icon.svg"
                    alt={t("event_card.icons.verified_alt")}
                    className={metaIconClass}
                  />
                )}
                {isByRequest && (
                  <EventMetaIcon
                    src="/assets/events/private_event.svg"
                    alt={t("event_card.icons.by_request_alt")}
                    className={metaIconClass}
                  />
                )}
              </div>
            )}
            <span
              ref={datetimeMeasureRef}
              aria-hidden="true"
              className={`${datetimeClass} pointer-events-none invisible absolute right-0 top-0 whitespace-nowrap font-semibold text-white text-opacity-50`}
              style={{ transform: `translateY(${datetimeYOffset}px)` }}
            />
            <time
              className={`${datetimeClass} font-semibold flex-shrink-0 whitespace-nowrap text-white text-opacity-50`}
              style={{ transform: `translateY(${datetimeYOffset}px)` }}
            >
              {type === "concept"
                ? t("event_card.concept_label", { defaultValue: "Concept" })
                : datetimeDisplay}
            </time>
          </div>
        </div>
      </div>
      {hasExpandableBlock ? (
        <div
          className={`grid transition-all duration-200 ease-out ${
            isSeriesExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <div className="px-0 pb-2 pt-1">
              <div className="pt-0">
                {expandableContent}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {showSeriesToggle ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleSeriesExpand?.();
          }}
          aria-expanded={isSeriesExpanded ? "true" : "false"}
          aria-label={t("event_card.series_toggle_aria")}
          className={`absolute bottom-0 left-1/2 z-20 flex h-5 min-w-[30px] -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full border border-white/60 px-2 shadow-[0_10px_20px_rgba(0,0,0,0.2)] ${fromMap ? "bg-[#2E67E8]" : "bg-[#0E1424]"}`}
        >
          <svg
            width="9"
            height="5"
            viewBox="0 0 12 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`transition-transform duration-200 text-white/80 ${isSeriesExpanded ? "rotate-180" : ""}`}
            aria-hidden="true"
          >
            <path
              d="M1 1L6 5L11 1"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}
    </article>
  );
};

export default EventCard;
